import React from 'react';
import { HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

interface SanskritTermProps {
  term: string;
  explanation: string;
  showIcon?: boolean;
}

export const SanskritTerm: React.FC<SanskritTermProps> = ({
  term,
  explanation,
  showIcon = true,
}) => {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 cursor-help border-b border-dotted border-muted-foreground/50 hover:border-muted-foreground/80 transition-colors">
            {term}
            {showIcon && <HelpCircle className="h-3 w-3 text-muted-foreground" />}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-md">
          <p className="leading-relaxed">{explanation}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const SANSKRIT_EXPLANATIONS = {
  tithi: "A lunar day marking the Moon's phase in the traditional Hindu calendar system. The lunar month consists of 30 Tithis, each representing a specific angular relationship between the Sun and Moon.",
  nakshatra: "One of 27 star constellations or lunar mansions used in Vedic astrology. These divide the ecliptic into equal segments, with each Nakshatra governed by a presiding deity and associated with specific qualities.",
  yoga: "A favorable alignment calculated from the combined positions of the Sun and Moon. The system includes 27 distinct Yogas, each carrying unique astrological significance and attributes.",
  karana: "A time period equal to half of a Tithi. The lunar month contains 11 different Karanas that cycle in a predetermined pattern, influencing the nature of activities during their duration.",
  vaara: "The weekday in the Vedic calendar system. In traditional astrology, each of the seven days is governed by a specific planetary deity that influences the day's characteristics.",
  paksha: "A two-week period in the lunar month. Shukla Paksha refers to the waxing phase when the Moon grows brighter, while Krishna Paksha is the waning phase when the Moon diminishes.",
  pada: "A subdivision of a Nakshatra, representing one-fourth of its span. With four Padas per Nakshatra, this system provides finer granularity for astrological analysis and timing.",
  rahuKala: "A time period considered unfavorable due to the influence of Rahu, the north lunar node. Traditional practice recommends postponing significant undertakings during these hours.",
  yamaGanda: "An inauspicious time interval linked to Yama, the deity of time and death. Initiating new ventures or important activities is traditionally avoided during this period.",
  gulikaKala: "A time frame associated with Gulika, considered unfavorable in Vedic tradition. This period is generally not recommended for performing auspicious ceremonies or important tasks.",
  abhijitMuhurta: "Regarded as the most favorable time window of the day, typically occurring near noon. This period is considered universally auspicious and suitable for commencing any significant activity.",
  ayanamsha: "The angular difference between the tropical (Western) and sidereal (Vedic) zodiac systems, resulting from the precession of the equinoxes. This value is essential for converting astronomical positions between these two coordinate systems.",
  muhurta: "An auspicious time segment for conducting specific rituals or activities. In traditional timekeeping, a Muhurta spans approximately 48 minutes, with the day divided into multiple such periods.",
};

export default SanskritTerm;

